<template>
  <div :id="modalId" class="modal fade" tabindex="-1" :aria-labelledby="`${modalId}Label`" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header d-flex align-items-center justify-content-between">
          <h5 class="modal-title" :id="`${modalId}Label`">{{ title }}</h5>
          <button type="button" class="btn btn-primary ms-auto" @click="handleDone">{{ doneLabel }}</button>
        </div>

        <div class="modal-body p-0">
          <div class="p-3 border-bottom">
            <input
              v-model="query"
              type="search"
              class="form-control form-control-lg w-100"
              :placeholder="searchPlaceholder"
            />
          </div>

          <div v-if="isLoading" class="d-flex justify-content-center align-items-center p-4 gap-2">
            <span class="fa fa-spinner fa-spin text-muted" aria-hidden="true"></span>
            <span class="text-muted small">{{ loadingLabel }}</span>
          </div>

          <div v-else-if="errorMessage" class="alert alert-danger m-3 mb-0">
            {{ errorMessage }}
          </div>

          <div v-else-if="query.length < 2" class="p-3 text-muted small">
            {{ searchHintLabel }}
          </div>

          <div v-else-if="guides.length === 0" class="p-3 text-muted">
            {{ noResultsLabel }}
          </div>

          <ul v-else class="list-group list-group-flush">
            <li
              v-for="guide in guides"
              :key="guide.id"
              class="list-group-item d-flex align-items-center gap-2 py-3"
            >
              <div class="form-check flex-grow-1 mb-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`guide-check-${guide.id}`"
                  :checked="guide.guide_station_id != null"
                  :disabled="savingGuideIds.has(guide.id)"
                  @change="toggleGuide(guide, $event.target.checked)"
                />
                <label class="form-check-label" :for="`guide-check-${guide.id}`">
                  {{ guide.title }}
                </label>
              </div>
              <span
                v-if="savingGuideIds.has(guide.id)"
                class="fa fa-spinner fa-spin text-muted"
                aria-hidden="true"
              ></span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch } from "vue"

const props = defineProps({
  modalId: { type: String, required: true },
  stationId: { type: String, required: true },
  searchUrl: { type: String, required: true },
  title: { type: String, default: "" },
  doneLabel: { type: String, default: "Done" },
  searchPlaceholder: { type: String, default: "Search guides..." },
  searchHintLabel: { type: String, default: "Enter at least 2 characters to search." },
  noResultsLabel: { type: String, default: "No guides found." },
  loadingLabel: { type: String, default: "Loading..." },
  searchErrorLabel: { type: String, default: "Search failed. Please try again." },
  saveErrorLabel: { type: String, default: "Could not save changes. Please try again." },
})

const query = ref("")
const guides = ref([])
const isLoading = ref(false)
const errorMessage = ref("")
const savingGuideIds = ref(new Set())
let hasChanges = false

let debounceTimer = null
let modalElement = null

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content || ""
}

watch(query, (newQuery) => {
  clearTimeout(debounceTimer)
  errorMessage.value = ""
  if (newQuery.length < 2) {
    guides.value = []
    return
  }
  debounceTimer = setTimeout(search, 300)
})

async function search() {
  isLoading.value = true
  errorMessage.value = ""
  try {
    const url = new URL(props.searchUrl, window.location.origin)
    url.searchParams.set("query", query.value)
    const response = await fetch(url.toString(), {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
    })
    if (!response.ok) throw new Error()
    const data = await response.json()
    guides.value = data.guides
  } catch (_) {
    errorMessage.value = props.searchErrorLabel
  } finally {
    isLoading.value = false
  }
}

async function toggleGuide(guide, checked) {
  const next = new Set(savingGuideIds.value)
  next.add(guide.id)
  savingGuideIds.value = next
  errorMessage.value = ""

  try {
    if (checked) {
      const response = await fetch(props.searchUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ guide_id: guide.id }),
      })
      if (!response.ok) throw new Error()
      const data = await response.json()
      guide.guide_station_id = data.guide_station_id
      hasChanges = true
    } else {
      const response = await fetch(`${props.searchUrl}/${guide.guide_station_id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error()
      guide.guide_station_id = null
      hasChanges = true
    }
  } catch (_) {
    errorMessage.value = props.saveErrorLabel
    await search()
  } finally {
    const done = new Set(savingGuideIds.value)
    done.delete(guide.id)
    savingGuideIds.value = done
  }
}

function handleDone() {
  if (modalElement) {
    const bsModal = window.bootstrap?.Modal?.getInstance(modalElement)
    bsModal?.hide()
  }
  if (hasChanges) {
    window.Turbo?.visit(window.location.href, { action: "replace" })
  }
}

onMounted(() => {
  modalElement = document.getElementById(props.modalId)
})

onBeforeUnmount(() => {
  modalElement = null
})
</script>
